import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  MapPin, Users, Package, AlertCircle, ArrowLeft, RefreshCw,
  CheckCircle2, XCircle, Truck, Sun, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import useProStatus from '../../hooks/useProStatus';
import ProProjectsPanel from './ProProjectsPanel';
import TimeCorrectionPanel from './TimeCorrectionPanel';

import { apiFetch } from '../../services/api';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Morgen-Dashboard für den Chef – „Das Erste, was der Chef im Büro öffnet".
 * Drei visuelle KPI-Widgets:
 *   1. Live auf den Baustellen
 *   2. Material-Freigaben
 *   3. Betriebs-Ampel (Krankheit + Produktivität)
 */

const trafficColors = {
  green: { bg: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500/40', label: 'Alles im grünen Bereich' },
  yellow: { bg: 'bg-yellow-500', text: 'text-yellow-400', border: 'border-yellow-500/40', label: 'Aufmerksamkeit erforderlich' },
  red: { bg: 'bg-rose-500', text: 'text-rose-400', border: 'border-rose-500/40', label: 'Kritisch – Handeln!' } };

const InhaberProDashboard = () => {
  const navigate = useNavigate();
  const pro = useProStatus();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/api/pro/dashboard/overview`, {
        headers: {} });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler');
      setOverview(data);
    } catch (err) {
      toast.error(err.message || 'Konnte Übersicht nicht laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (pro.active) load(); }, [pro.active]);

  const decide = async (orderId, action) => {
    try {
      const res = await apiFetch(`${API}/api/pro/material/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId, action }) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Aktion fehlgeschlagen');
      toast.success(`Bestellung ${data.status}`);
      load();
    } catch (err) {
      toast.error(err.message || 'Fehler');
    }
  };

  // Not-Pro-Zustand
  if (!pro.loading && !pro.active) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gray-950 border-yellow-500/40">
          <CardContent className="p-6 text-center space-y-4">
            <TrendingUp className="h-12 w-12 text-yellow-500 mx-auto" />
            <h2 className="text-xl font-bold">SmartVolt Pro nicht aktiv</h2>
            <p className="text-gray-400 text-sm">Für dieses Dashboard benötigst du das Pro-Abo.</p>
            <Button onClick={() => navigate('/pro')} className="bg-yellow-500 text-black">Upgrade ansehen</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sites = overview?.live_sites || [];
  const orders = overview?.pending_material_orders || [];
  const hr = overview?.hr || { sick_today: 0, total_employees: 0, sick_ratio_pct: 0, traffic_light: 'green', active_time_entries: 0 };
  const traffic = trafficColors[hr.traffic_light] || trafficColors.green;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-950 text-white pb-10">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/70 border-b border-yellow-500/20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/chef-cockpit')} className="text-gray-300 hover:text-yellow-500" data-testid="pro-dash-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Cockpit
          </Button>
          <div className="text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Guten Morgen · SmartVolt <span className="text-yellow-500">Pro</span></p>
            <h1 className="text-white font-bold">Chef-Live-Dashboard</h1>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="border-gray-700" data-testid="pro-dash-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="pro-chef-dashboard">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* WIDGET 1: Live auf den Baustellen */}
          <Card className="bg-gradient-to-br from-gray-950 to-black border-sky-500/30 lg:col-span-2" data-testid="widget-live-sites">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-sky-400" /> Live auf den Baustellen
                  </CardTitle>
                  <CardDescription className="text-gray-400">Wer ist wo eingestempelt – gerade jetzt.</CardDescription>
                </div>
                <Badge className="bg-sky-500/20 text-sky-300 border border-sky-500/40">
                  {hr.active_time_entries || 0} aktiv
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {sites.length === 0 ? (
                <p className="text-center py-8 text-gray-500 text-sm">Aktuell niemand eingestempelt.</p>
              ) : (
                <div className="space-y-3">
                  {sites.map((s) => (
                    <div key={s.project.id} className="p-3 rounded-lg bg-black/40 border border-gray-800 flex items-start justify-between gap-3" data-testid={`live-site-${s.project.id}`}>
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate">{s.project.name}</p>
                        {s.project.address && <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{s.project.address}</p>}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {s.employees.map((n, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-200 border border-sky-500/30">{n}</span>
                          ))}
                        </div>
                      </div>
                      <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                        <Users className="h-3 w-3 mr-1" /> {s.count}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* WIDGET 3: Betriebs-Ampel */}
          <Card className={`bg-gradient-to-br from-gray-950 to-black ${traffic.border} border-2`} data-testid="widget-traffic-light">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Sun className={`h-5 w-5 ${traffic.text}`} /> Betriebs-Ampel
              </CardTitle>
              <CardDescription className="text-gray-400">Krankheit &amp; Produktivität heute</CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-3">
              <div className={`w-24 h-24 mx-auto rounded-full ${traffic.bg} shadow-lg flex items-center justify-center`}>
                <span className="text-black font-black text-3xl">{hr.sick_ratio_pct}<span className="text-xl">%</span></span>
              </div>
              <p className={`font-semibold ${traffic.text}`}>{traffic.label}</p>
              <div className="grid grid-cols-2 gap-3 text-sm pt-2">
                <div>
                  <p className="text-2xl font-bold text-white">{hr.sick_today}</p>
                  <p className="text-xs text-gray-500">krank heute</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{hr.total_employees}</p>
                  <p className="text-xs text-gray-500">Team gesamt</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* WIDGET 2: Material-Freigaben */}
        <Card className="bg-gradient-to-br from-gray-950 to-black border-yellow-500/30" data-testid="widget-material-orders">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Package className="h-5 w-5 text-yellow-400" /> Material-Freigaben
                </CardTitle>
                <CardDescription className="text-gray-400">Warenkörbe deiner Monteure, die auf deine Freigabe warten.</CardDescription>
              </div>
              <Badge className={orders.length > 0 ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40' : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'}>
                {orders.length > 0 ? `${orders.length} offen` : 'Nichts offen'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {orders.length === 0 ? (
              <p className="text-center py-6 text-gray-500 text-sm">Alle Bestellungen sind auf Kurs. 🎉</p>
            ) : (
              <div className="space-y-3">
                {orders.map((o) => (
                  <div key={o._id} className="p-3 rounded-lg bg-black/40 border border-gray-800" data-testid={`order-${o._id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-white font-medium">Bestellung #{o._id.slice(-6)}</p>
                        <p className="text-xs text-gray-500">
                          {(o.lines || []).length} Position(en) · gesamt {new Intl.NumberFormat('de-DE', {style:'currency',currency:'EUR'}).format(o.total_eur || 0)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => decide(o._id, 'reject')} className="border-rose-500/40 text-rose-400" data-testid={`reject-${o._id}`}>
                          <XCircle className="h-4 w-4 mr-1" /> Ablehnen
                        </Button>
                        <Button size="sm" onClick={() => decide(o._id, 'approve')} className="bg-emerald-500 hover:bg-emerald-400 text-black" data-testid={`approve-${o._id}`}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Freigeben
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => decide(o._id, 'mark_ordered')} className="border-yellow-500/40 text-yellow-400" data-testid={`ordered-${o._id}`}>
                          <Truck className="h-4 w-4 mr-1" /> Bestellt
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {loading && (
          <div className="text-center text-xs text-gray-600 flex items-center justify-center gap-2">
            <AlertCircle className="h-3 w-3" /> Daten werden geladen…
          </div>
        )}

        {/* Baustellen-Verwaltung (CRUD) */}
        <ProProjectsPanel />

        {/* Zeit-Korrektur (manuell) */}
        <TimeCorrectionPanel />
      </main>
    </div>
  );
};

export default InhaberProDashboard;
